import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Eye } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, EmptyState } from "../../components/Neon";
import { fmtDateTime } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

export default function AdminVerifications() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  const load = () => api.get("/admin/verifications").then(({ data }) => setList(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const decide = async (uid, approve) => {
    try { await api.post(`/admin/verifications/${uid}`, { approve }); toast.success(approve ? "Approved." : "Rejected."); setView(null); load(); }
    catch (e) { toast.error(getErr(e)); }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-verifications">
      <h2 className="font-display uppercase text-2xl text-white">KYC Verifications</h2>
      {list.length === 0 ? <div className="mt-4"><EmptyState title="No pending verifications" /></div> : (
        <NeonCard variant="soft" className="mt-4" innerClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[var(--text-muted)] border-b border-white/10"><th className="p-3">Name</th><th>Email</th><th>Doc</th><th>Uploaded</th><th className="text-right p-3">Actions</th></tr></thead>
              <tbody>
                {list.map((v) => (
                  <tr key={v.user_id} className="border-b border-white/5">
                    <td className="p-3 text-white">{v.full_name}</td>
                    <td className="text-[var(--text-muted)]">{v.email}</td>
                    <td className="capitalize">{v.doc_type}</td>
                    <td className="text-[var(--text-muted)]">{fmtDateTime(v.uploaded_at)}</td>
                    <td className="p-3"><div className="flex items-center justify-end gap-1">
                      <button onClick={() => setView(v)} className="p-2 rounded-lg hover:bg-white/10 text-[var(--neon-cyan)]"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => decide(v.user_id, true)} className="p-2 rounded-lg hover:bg-white/10 text-[var(--neon-green)]" data-testid={`approve-kyc-${v.user_id}`}><Check className="h-4 w-4" /></button>
                      <button onClick={() => decide(v.user_id, false)} className="p-2 rounded-lg hover:bg-white/10 text-[var(--neon-pink)]"><X className="h-4 w-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </NeonCard>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{view?.full_name} · {view?.doc_type}</DialogTitle><DialogDescription className="text-[var(--text-muted)]">Review the uploaded identity document, then approve or reject.</DialogDescription></DialogHeader>
          {view?.document_base64?.startsWith("data:image") ? (
            <img src={view.document_base64} alt="KYC document" className="w-full rounded-lg border border-white/10" />
          ) : <p className="text-[var(--text-muted)] text-sm break-all">Document data: {view?.document_base64?.slice(0, 80)}…</p>}
          <div className="flex gap-3 justify-end">
            <NeonButton variant="outlineGreen" onClick={() => decide(view.user_id, true)}>Approve</NeonButton>
            <NeonButton variant="outlinePink" onClick={() => decide(view.user_id, false)}>Reject</NeonButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
